// The console chrome. `/console` and `/console/:slug` live inside this.
//
// THE PROJECT SWITCHER IS TOP-LEVEL — it sits in the console's own top bar, next
// to the wordmark, above everything. It used to be a `<Select>` rendered *inside*
// the embedded server panel, which put "which project am I looking at?" — the
// single most important piece of state on the page — below the fold of the thing
// it controls, and made it look like a setting of the panel rather than the
// address of the page. Switching project is navigation, so it belongs in the
// navigation.
//
// The switcher lists every project you can OPERATE, platform included: the
// platform is just another project, so its console is `/console/platform` and it
// appears in the same list as everything else. No special case.

import { Badge, Menu, UnstyledButton } from "@mantine/core";
import {
	IconChevronDown,
	IconExternalLink,
	IconLayoutGrid,
	IconLogout,
	IconPlus,
	IconRobot,
	IconUserCircle,
	IconUsersGroup,
} from "@tabler/icons-react";
import { Fragment } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FeedbackWidget } from "../../components/FeedbackWidget.tsx";
import { Wordmark } from "../../components/Footer.tsx";
import { c, font, radius } from "../../design/tokens.ts";
import { useProjectTree } from "../../hooks/useProjectTree.ts";
import { projectPath, projectUrl } from "../../lib/hosts.ts";
import { useSession } from "../../session/SessionProvider.tsx";

/** Your operator role on ONE project, read straight from the platform-pool
 *  groups already in the token — no network call, and no per-project lookup.
 *  The root project is the exception the whole codebase carries: its operator
 *  groups are its NATIVE `console:owner|admin`, not `console:operator-*`. */
function roleOnProject(groups: readonly string[] | undefined, slug: string): string | null {
	if (!groups?.length) return null;
	const prefixes = [`${slug}:operator-`, `${slug}:`];
	for (const p of prefixes) {
		const hit = groups.find((g) => g.startsWith(p));
		if (hit) return hit.slice(p.length);
	}
	return null;
}

export function ConsoleLayout({ children }: { children: React.ReactNode }) {
	const { slug } = useParams();
	const navigate = useNavigate();
	const { me, signOut } = useSession();
	const projects = useProjectTree();

	const current = projects.data?.find((p) => p.slug === slug) ?? null;

	return (
		<div
			style={{
				fontFamily: font.sans,
				color: c.ink,
				background: c.bgAlt,
				minHeight: "100vh",
				display: "flex",
				flexDirection: "column",
			}}
		>
			<header
				style={{
					background: c.bg,
					borderBottom: `1px solid ${c.border}`,
					position: "sticky",
					top: 0,
					zIndex: 200,
				}}
			>
				<div
					className="s3p-console-bar"
					style={{
						display: "flex",
						alignItems: "center",
						gap: 12,
						padding: "10px 14px",
						minWidth: 0,
					}}
				>
					<Link to="/" style={{ textDecoration: "none", flexShrink: 0 }}>
						<Wordmark size={18} />
					</Link>

					<span className="s3p-hide-sm" style={{ color: c.border }}>
						/
					</span>

					{/* ── the switcher ── */}
					<Menu shadow="md" width={280} position="bottom-start">
						<Menu.Target>
							<UnstyledButton
								style={{
									display: "flex",
									alignItems: "center",
									gap: 8,
									padding: "6px 12px",
									borderRadius: radius.pill,
									border: `1px solid ${c.border}`,
									background: c.bg,
									fontFamily: font.sans,
									fontSize: 13.5,
									fontWeight: 700,
									color: c.ink,
								}}
							>
								{current ? (
									<>
										<span className="s3p-hide-sm">{current.name || current.slug}</span>
										{/* Plain mono text, NOT a chip — see .s3p-slug-chip. A pill
										    inside this button's own pill reads as two controls, and
										    only the outer one does anything. The typeface carries the
										    distinction between the id and the friendly name beside it. */}
										<span className="s3p-slug-chip">{current.slug}</span>
										{/* Which ENVIRONMENT you are operating, stated loudly and
										    never hidden on mobile. Every destructive control in this
										    console (Database, Users, Roles, teardown) acts on whatever
										    this says — mistaking prod for dev is the one failure this
										    feature actually introduces, so the label is styled to be
										    noticed rather than to blend in. */}
										{current.environmentOf && (
											<Badge size="xs" variant="filled" color="orange">
												{current.environment}
											</Badge>
										)}
									</>
								) : (
									<span style={{ color: c.muted }}>All apps</span>
								)}
								<IconChevronDown size={14} color={c.faint} />
							</UnstyledButton>
						</Menu.Target>

						<Menu.Dropdown>
							<Menu.Item
								leftSection={<IconLayoutGrid size={15} />}
								onClick={() => navigate("/")}
							>
								All apps
							</Menu.Item>
							{projects.roots.length > 0 && <Menu.Divider />}
							{projects.roots.map((p) => (
								// Base project, then its environments indented beneath it —
								// switching environment stays inside the project rather than
								// looking like a jump to an unrelated app.
								<Fragment key={p.slug}>
									<Menu.Item
										onClick={() => navigate(projectPath(p.slug))}
										rightSection={
											<span style={{ fontFamily: font.mono, fontSize: 11, color: c.faint }}>
												{p.slug}
											</span>
										}
									>
										{p.name || p.slug}
									</Menu.Item>
									{(projects.envs.get(p.slug) ?? []).map((e) => (
										<Menu.Item
											key={e.slug}
											onClick={() => navigate(projectPath(e.slug))}
											pl={30}
											rightSection={
												<span
													style={{ fontFamily: font.mono, fontSize: 11, color: c.faint }}
												>
													{e.slug}
												</span>
											}
										>
											<span style={{ color: c.muted }}>{e.environment}</span>
										</Menu.Item>
									))}
								</Fragment>
							))}
							<Menu.Divider />
							<Menu.Item
								leftSection={<IconPlus size={15} />}
								onClick={() => navigate("/signup")}
							>
								New app
							</Menu.Item>
						</Menu.Dropdown>
					</Menu>

					{current && (
						<a
							href={projectUrl(current.slug)}
							target="_blank"
							rel="noreferrer"
							style={{
								display: "flex",
								alignItems: "center",
								gap: 5,
								fontSize: 13,
								fontWeight: 600,
								color: c.muted,
								textDecoration: "none",
							}}
						>
							<span className="s3p-hide-sm">Open app</span>
							<IconExternalLink size={13} />
						</a>
					)}

					<div style={{ flex: 1 }} />

					<span
						className="s3p-hide-sm"
						style={{
							fontFamily: font.mono,
							fontSize: 11.5,
							color: c.faint,
							display: "flex",
							alignItems: "center",
							gap: 7,
							whiteSpace: "nowrap",
						}}
					>
						<span
							style={{ width: 6, height: 6, borderRadius: "50%", background: c.green }}
						/>
						eu-central-1
					</span>

					<Menu shadow="md" width={220} position="bottom-end">
						<Menu.Target>
							<UnstyledButton
								style={{
									display: "flex",
									alignItems: "center",
									gap: 8,
									padding: "6px 12px",
									borderRadius: radius.pill,
									border: `1px solid ${c.border}`,
									fontSize: 13,
									fontWeight: 600,
									color: c.ink,
									fontFamily: font.sans,
								}}
							>
								<IconUserCircle size={17} className="s3p-show-sm" />
							<span
								className="s3p-hide-sm"
								style={{
									maxWidth: 180,
									overflow: "hidden",
									textOverflow: "ellipsis",
									whiteSpace: "nowrap",
								}}
							>
								{me?.email || me?.username || "Account"}
							</span>
							<IconChevronDown size={14} color={c.faint} className="s3p-hide-sm" />
							</UnstyledButton>
						</Menu.Target>
						<Menu.Dropdown>
							{/* WHO YOU ARE, where people look for it. This used to print
							    `me.role` — the highest group across the WHOLE platform
							    account, which on a project you merely view still read
							    "owner". What matters standing on a project page is your
							    role HERE, so it's derived from the platform groups for
							    this slug. */}
							<Menu.Label>{me?.email || me?.username || "Account"}</Menu.Label>
							{current && (
								<Menu.Item
									leftSection={<IconUsersGroup size={15} />}
									onClick={() => navigate(projectPath(current.slug, "team"))}
								>
									<span style={{ fontSize: 12.5 }}>
										{roleOnProject(me?.groups, current.slug) ?? "no role"} on{" "}
										<b>{current.slug}</b>
									</span>
									<div style={{ fontSize: 11, color: c.faint }}>view the team</div>
								</Menu.Item>
							)}
							<Menu.Divider />
							{/* The agent code moved off the app list onto its own page, so
							    the way back to it has to be somewhere you always are. The
							    account menu is where it belongs: it's a credential of the
							    PERSON, not of the project you happen to be looking at. */}
							<Menu.Item
								leftSection={<IconRobot size={15} />}
								onClick={() => navigate("/agent")}
							>
								Connect an agent
							</Menu.Item>
							<Menu.Divider />
							<Menu.Item leftSection={<IconLogout size={15} />} onClick={() => signOut()}>
								Sign out
							</Menu.Item>
						</Menu.Dropdown>
					</Menu>
				</div>
			</header>

			<main style={{ flex: 1, minWidth: 0 }}>{children}</main>

			{/* Lives in the CHROME, not on a settings page: the moment someone wants
			    to report a gap is the moment they hit it, and any navigation between
			    those two loses most of the reports. */}
			<FeedbackWidget />
		</div>
	);
}
